import { Alert, Flex, Form, Modal, Select, Tag, Typography } from 'antd'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { UserGroup } from 'src/api/main'

import {
  partitionRolesByApp,
  useAppRoleOptions
} from '../hooks/useAppGroupRoles'

const { Text } = Typography

type FormValues = { group: number; roles: number[] }

type Props = {
  open: boolean
  organizationId: number
  applicationId: number
  group: UserGroup | null
  candidates?: UserGroup[]
  saving?: boolean
  onCancel: () => void
  onSubmit: (groupId: number, roleIds: number[]) => void
}

const ForeignRolesNotice = ({
  group,
  applicationId
}: {
  group: UserGroup
  applicationId: number
}) => {
  const { t } = useTranslation()
  const { foreign } = partitionRolesByApp(group, applicationId)

  if (foreign.length === 0) return null

  return (
    <Alert
      type="warning"
      showIcon
      title={t('workspace.apps.groups.modal.alsoGrants')}
      description={
        <Flex gap={4} wrap="wrap" style={{ marginTop: 4 }}>
          {foreign.map((role) => (
            <Tag key={role.id} style={{ marginInlineEnd: 0 }}>
              {role.name}
            </Tag>
          ))}
        </Flex>
      }
    />
  )
}

export const AppGroupRolesModal = ({
  open,
  organizationId,
  applicationId,
  group,
  candidates = [],
  saving,
  onCancel,
  onSubmit
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const { roleOptions, isLoading } = useAppRoleOptions(
    organizationId,
    applicationId,
    open
  )

  const isPicking = group == null

  const initialValues = useMemo<Partial<FormValues>>(
    () =>
      group
        ? {
            group: group.id,
            roles: partitionRolesByApp(group, applicationId).own.map(
              (r) => r.id
            )
          }
        : { roles: [] },
    [applicationId, group]
  )

  const selectedGroupId = Form.useWatch('group', form)
  const noticeGroup =
    group ?? candidates.find((candidate) => candidate.id === selectedGroupId)

  const handleFinish = (values: FormValues) => {
    const groupId = group?.id ?? values.group
    if (groupId == null) return
    onSubmit(groupId, values.roles ?? [])
  }

  const candidateOptions = useMemo(
    () =>
      candidates.map((candidate) => ({
        value: candidate.id,
        label: candidate.name
      })),
    [candidates]
  )

  return (
    <Modal
      open={open}
      centered
      destroyOnHidden
      title={t(
        isPicking
          ? 'workspace.apps.groups.modal.addTitle'
          : 'workspace.apps.groups.modal.editTitle'
      )}
      okText={t('workspace.apps.groups.modal.submit')}
      cancelText={t('workspace.apps.groups.modal.cancel')}
      okButtonProps={{ loading: saving }}
      cancelButtonProps={{ disabled: saving }}
      onOk={() => form.submit()}
      onCancel={() => {
        if (!saving) onCancel()
      }}
    >
      <Flex vertical gap={12} style={{ paddingTop: 8 }}>
        <Alert
          type="info"
          showIcon
          title={t('workspace.apps.groups.modal.accessNotice')}
        />

        <Form<FormValues>
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={initialValues}
          onFinish={handleFinish}
        >
          {isPicking ? (
            <Form.Item
              name="group"
              label={t('workspace.apps.groups.modal.groupLabel')}
              rules={[
                {
                  required: true,
                  message: t('workspace.apps.groups.modal.groupRequired')
                }
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={candidateOptions}
                placeholder={t('workspace.apps.groups.modal.groupPlaceholder')}
                notFoundContent={t('workspace.apps.groups.modal.noCandidates')}
              />
            </Form.Item>
          ) : (
            <Form.Item label={t('workspace.apps.groups.modal.groupLabel')}>
              <Text strong>{group.name}</Text>
            </Form.Item>
          )}

          <Form.Item
            name="roles"
            label={t('workspace.apps.groups.modal.rolesLabel')}
            extra={t('workspace.apps.groups.modal.rolesHint')}
          >
            <Select
              mode="multiple"
              allowClear
              loading={isLoading}
              options={roleOptions}
              optionFilterProp="label"
              placeholder={t('workspace.apps.groups.modal.rolesPlaceholder')}
            />
          </Form.Item>
        </Form>

        {noticeGroup ? (
          <ForeignRolesNotice
            group={noticeGroup}
            applicationId={applicationId}
          />
        ) : null}
      </Flex>
    </Modal>
  )
}
